"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import NewsletterForm from "@/components/NewsletterForm";

const floatingBooks = [
{
  id: "book-1",
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1877bd5de-1765240271373.png",
  alt: "Colorful alphabet learning book with illustrated animals for toddlers",
  label: "ABC Animals",
  tag: "Ages 2–5",
  rating: "4.9",
  price: "$12.99",
  color: "#FFB5A7",
  animClass: "float-a",
  position: "top-16 right-[8%] lg:right-[12%]",
  rotate: "-rotate-3"
},
{
  id: "book-2",
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_16ea3faad-1764845124383.png",
  alt: "Numbers and counting coloring book for kindergarten children",
  label: "123 Fun",
  tag: "Ages 3–6",
  rating: "5.0",
  price: "$10.99",
  color: "#A8DADC",
  animClass: "float-b",
  position: "top-36 right-[2%] lg:right-[4%]",
  rotate: "rotate-4"
},
{
  id: "book-3",
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_14bb11057-1768659036248.png",
  alt: "Dreamy coloring book with magical creatures and fairy tale scenes",
  label: "Magic World",
  tag: "Ages 4–8",
  rating: "4.8",
  price: "$14.99",
  color: "#C8B6E2",
  animClass: "float-c",
  position: "bottom-24 right-[10%] lg:right-[15%]",
  rotate: "-rotate-1"
}];


const stats = [
{ value: "50K+", label: "Happy Kids", icon: "FaceSmileIcon" },
{ value: "4.9★", label: "Parent Rating", icon: "StarIcon" },
{ value: "30+", label: "Countries", icon: "GlobeAltIcon" }];


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = heroRef.current?.querySelectorAll(".hero-reveal");
    if (!els) return;
    els.forEach((el, i) => {
      (el as HTMLElement).style.animationDelay = `${i * 0.15}s`;
      el.classList.add("animate-fade-up");
    });
  }, []);

  return (
    <section
      ref={heroRef}
      className="hero-bg relative min-h-screen flex items-center overflow-hidden pt-24 pb-16"
      aria-label="Hero — My Happy Place Digital">
      
      {/* Decorative shapes */}
      <div className="absolute top-20 left-10 w-20 h-20 rounded-full opacity-30 blur-xl" style={{ background: "var(--peach)" }} />
      <div className="absolute top-1/3 left-1/4 w-40 h-40 rounded-full opacity-20 blur-2xl" style={{ background: "var(--lavender)" }} />
      <div className="absolute bottom-20 left-16 w-28 h-28 rounded-full opacity-25 blur-xl" style={{ background: "var(--mint)" }} />
      <div className="absolute top-10 right-1/3 w-12 h-12 rounded-full opacity-50 star-pulse" style={{ background: "var(--gold)" }} />

      {/* Floating stars */}
      {["top-32 left-1/3", "top-1/2 left-8", "bottom-32 left-1/3"].map((pos, i) =>
      <div
        key={i}
        className={`absolute ${pos} text-2xl star-pulse`}
        style={{ animationDelay: `${i * 0.7}s` }}
        aria-hidden="true">
        
          ✨
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left — Text */}
          <div className="text-center lg:text-left">
            <div className="hero-reveal opacity-0 inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-lavender/40 rounded-full px-4 py-2 text-xs font-bold text-plum mb-6 shadow-dreamy">
              <span className="shimmer-badge rounded-full px-2 py-0.5 text-plum text-xs font-black">NEW</span>
              2026 Collection — Now Available Worldwide 🌍
            </div>

            <h1 className="hero-reveal opacity-0 font-fraunces text-5xl sm:text-6xl lg:text-7xl font-black text-plum leading-[0.95] tracking-tight mb-6">
              Where Little
              <span className="block italic text-plum-light">Minds</span>
              <span className="block" style={{ color: "var(--rose)" }}>Come Alive.</span>
            </h1>

            <p className="hero-reveal opacity-0 text-plum/70 text-lg leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
              Magical coloring books, reading books, alphabet adventures, and number journeys — crafted to spark curiosity and creativity in children aged 2–8 across the globe. 🎨
            </p>

            <div className="hero-reveal opacity-0 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <Link href="/products" className="btn-primary px-8 py-4 text-base flex items-center gap-2 justify-center min-h-[48px]">
                <Icon name="ShoppingBagIcon" size={20} variant="solid" />
                Explore Books
              </Link>
              <a
                href="https://wa.me/256764590167"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary px-8 py-4 text-base flex items-center gap-2 justify-center min-h-[48px]">
                
                <Icon name="ChatBubbleLeftRightIcon" size={18} variant="solid" className="text-green-500" />
                Ask on WhatsApp
              </a>
            </div>

            {/* Stats */}
            <div className="hero-reveal opacity-0 flex flex-wrap gap-6 justify-center lg:justify-start">
              {stats.map((s) =>
              <div key={s.value} className="text-center min-w-[80px]">
                  <div className="font-fraunces text-2xl sm:text-3xl font-black text-plum">{s.value}</div>
                  <div className="text-xs sm:text-sm text-plum/55 font-medium">{s.label}</div>
                </div>
              )}
            </div>

            {/* Newsletter Subscription */}
            <div className="hero-reveal opacity-0 mt-10 p-6 rounded-2xl bg-white/70 backdrop-blur-sm border border-lavender/40 shadow-dreamy">
              <div className="flex items-start gap-3 mb-4">
                <div className="text-2xl">📧</div>
                <div>
                  <h3 className="text-plum font-bold text-lg mb-1">Stay Updated!</h3>
                  <p className="text-plum/70 text-sm">
                    Get exclusive book releases, special offers, and learning tips delivered to your inbox.
                  </p>
                </div>
              </div>
              <NewsletterForm variant="hero" />
            </div>
          </div>

          {/* Right — Floating books */}
          <div className="relative h-[480px] lg:h-[580px] hidden sm:block">
            {floatingBooks.map((book, index) =>
            <div
              key={book.id}
              className={`absolute ${book.position} ${book.animClass} ${book.rotate} w-36 lg:w-44`}
              aria-hidden="true">
              
                <div
                className="rounded-2xl overflow-hidden shadow-dreamy-xl border-4 border-white"
                style={{ borderColor: book.color }}>
                
                  <div className="relative h-44 lg:h-56 overflow-hidden">
                    <AppImage
                    src={book.src}
                    alt={book.alt}
                    fill
                    priority={index === 0}
                    sizes="(max-width: 640px) 0vw, (max-width: 1024px) 144px, 176px"
                    className="object-cover" />
                  
                  </div>
                  <div className="glass px-3 py-2 text-center">
                    <div className="text-xs font-black text-plum">{book.label}</div>
                    <div className="text-xs text-plum/60">{book.tag}</div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs stars">★ {book.rating}</span>
                      <span className="text-xs font-bold text-plum-light">{book.price}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Center big glow */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl opacity-40"
              style={{ background: "radial-gradient(circle, #C8B6E2, #FFB5A7, #A8DADC)" }}
              aria-hidden="true" />
            

            {/* Spinning ring */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full border-2 border-dashed border-lavender/40 spin-slow"
              aria-hidden="true" />
            
          </div>
        </div>
      </div>

      {/* Wave bottom */}
      <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 60L60 50C120 40 240 20 360 15C480 10 600 20 720 25C840 30 960 30 1080 25C1200 20 1320 10 1380 5L1440 0V60H0Z" fill="#FFF8F0" />
        </svg>
      </div>
    </section>);

}